
import { Link } from "react-router-dom";
import { MessageCircle, ImagePlus, Heart, Brain } from "lucide-react";
import { motion } from "framer-motion";

const tools = [
  {
    title: "DM the Universe",
    description: "Chat with the universe to clarify your goals, craft affirmations, and manifest your reality through guided conversation.",
    icon: MessageCircle,
    href: "/dashboard/chat",
    gradient: "from-cosmic-500 to-cosmic-700",
    glowColor: "cosmic-500",
  },
  {
    title: "Vision Board",
    description: "Upload your photos, generate AI images, and build a visual collection of everything you're manifesting into your life.",
    icon: ImagePlus,
    href: "/dashboard/vision-board",
    gradient: "from-cosmic-400 to-purple-600",
    glowColor: "cosmic-400",
  },
  {
    title: "Gratitude Practice",
    description: "Walk through your vision board one image at a time and express gratitude as if it's already yours.",
    icon: Heart,
    href: "/dashboard/gratitude",
    gradient: "from-pink-500 to-cosmic-500",
    glowColor: "pink-500",
  },
  {
    title: "Mindful Chat",
    description: "Talk through what's on your mind. A supportive space to process your thoughts and gently shift your perspective.",
    icon: Brain,
    href: "/dashboard/mindful-chat",
    gradient: "from-teal-500 to-cosmic-600",
    glowColor: "teal-500",
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const DashboardHome = () => {
  return (
    <div className="flex-1 flex flex-col items-center justify-start md:justify-center px-4 py-8 md:py-20 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-12"
      >
        <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-3">
          What would you like to do?
        </h1>
        <p className="text-muted-foreground text-base md:text-lg max-w-md mx-auto">
          Choose a practice to continue your manifestation journey.
        </p>
      </motion.div>

      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 w-full max-w-5xl"
      >
        {tools.map((tool) => (
          <motion.div key={tool.title} variants={item}>
            <Link
              to={tool.href}
              className="group block cosmic-card p-6 md:p-8 h-full transition-all duration-300 hover:border-cosmic-400/30 hover:scale-[1.02] hover:shadow-[0_0_30px_-5px_hsl(var(--primary)/0.3)]"
            >
              <div
                className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${tool.gradient} mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}
              >
                <tool.icon className="w-6 h-6 text-white" />
              </div>

              <h2 className="text-xl font-semibold text-foreground mb-2 group-hover:text-cosmic-300 transition-colors">
                {tool.title}
              </h2>

              <p className="text-sm text-muted-foreground leading-relaxed">
                {tool.description}
              </p>
            </Link>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default DashboardHome;
